import { useEffect, useRef, useState } from "react";
import authLogoLight from "@/assets/auth-logo-light.png";
import authLogoDark from "@/assets/auth-logo-dark.png";
import { useTheme } from "@/components/ThemeProvider";

type PathInfo = { d: string; color: string; delay: number; len: number };

const INPUTS = [
  { label: "PDFs, DOCs & Slides", dot: "bg-brand-red", stroke: "hsl(var(--color-red))" },
  { label: "Audios & Videos", dot: "bg-orange", stroke: "hsl(var(--color-orange))" },
  { label: "YouTube & Web URLs", dot: "bg-purple-accent", stroke: "hsl(var(--purple-accent))" },
];

const OUTPUTS = [
  "AI Chat Tutor + Citations",
  "Flashcards · Quizzes · Exams",
  "8 Summary Templates",
  "Mastery Courses · Rooms",
];

const OUT_STROKE = "hsl(var(--color-green))";

export const Integrations = () => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const inPillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const outPillRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [inPaths, setInPaths] = useState<PathInfo[]>([]);
  const [outPaths, setOutPaths] = useState<PathInfo[]>([]);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current || !nodeRef.current) return;
      const cRect = containerRef.current.getBoundingClientRect();
      const nodeRect = nodeRef.current.getBoundingClientRect();

      const nodeLX = nodeRect.left - cRect.left;
      const nodeRX = nodeRect.right - cRect.left;
      const nodeCY = nodeRect.top - cRect.top + nodeRect.height / 2;

      const newIn: PathInfo[] = [];
      INPUTS.forEach((cfg, i) => {
        const pill = inPillRefs.current[i];
        if (!pill) return;
        const r = pill.getBoundingClientRect();
        const fx = r.right - cRect.left;
        const fy = r.top - cRect.top + r.height / 2;
        const cpx = fx + (nodeLX - fx) * 0.5;
        const d = `M ${fx},${fy} C ${cpx},${fy} ${cpx},${nodeCY} ${nodeLX},${nodeCY}`;
        const len = Math.hypot(nodeLX - fx, nodeCY - fy) * 1.6;
        newIn.push({ d, color: cfg.stroke, delay: i * 0.45, len });
      });
      setInPaths(newIn);

      const newOut: PathInfo[] = [];
      OUTPUTS.forEach((_, i) => {
        const pill = outPillRefs.current[i];
        if (!pill) return;
        const r = pill.getBoundingClientRect();
        const tx = r.left - cRect.left;
        const ty = r.top - cRect.top + r.height / 2;
        const cpx = nodeRX + (tx - nodeRX) * 0.5;
        const d = `M ${nodeRX},${nodeCY} C ${cpx},${nodeCY} ${cpx},${ty} ${tx},${ty}`;
        const len = Math.hypot(tx - nodeRX, ty - nodeCY) * 1.6;
        newOut.push({ d, color: OUT_STROKE, delay: i * 0.45 + 0.3, len });
      });
      setOutPaths(newOut);
    };

    const t = setTimeout(measure, 350);
    window.addEventListener("resize", measure);
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, []);

  const allPaths = [...inPaths, ...outPaths];

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-6 md:px-4">
        {/* Header */}
        <div className="flex flex-col items-center justify-center mb-12 md:mb-16">
          <p className="text-[11px] md:text-xs font-bold tracking-[0.25em] uppercase text-muted-foreground mb-3">
            The Workflow
          </p>
          <h2 className="text-2xl md:text-4xl font-medium text-center text-foreground max-w-4xl leading-snug px-2">
            Upload your course files. Get a full study toolkit — in minutes.
          </h2>
          <div className="w-12 md:w-16 h-1 rounded-full bg-primary mt-5 md:mt-6" />
        </div>

        {/* Diagram */}
        <div
          ref={containerRef}
          className="relative max-w-6xl mx-auto"
        >
          {/* Wires (desktop only) */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none hidden md:block"
            style={{ zIndex: 0 }}
          >
            <defs>
              <style>{`
                @keyframes wire-travel {
                  0%   { stroke-dashoffset: var(--len); opacity: 0; }
                  8%   { opacity: 1; }
                  92%  { opacity: 1; }
                  100% { stroke-dashoffset: 0; opacity: 0; }
                }
                .wire-travel { animation: wire-travel 2.6s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                  .wire-travel { animation: none; opacity: 0; }
                }
              `}</style>
            </defs>
            {allPaths.map(({ d, color, delay, len }, i) => (
              <g key={i}>
                <path
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeOpacity={0.45}
                  strokeDasharray="6 7"
                />
                <path
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeDasharray={`14 ${len}`}
                  className="wire-travel"
                  style={
                    {
                      "--len": len,
                      animationDelay: `${delay}s`,
                    } as React.CSSProperties
                  }
                />
              </g>
            ))}
          </svg>

          {/* Grid */}
          <div
            className="relative grid gap-10 md:gap-0 items-center"
            style={{ gridTemplateColumns: "minmax(0,1fr)" }}
          >
            <div
              className="grid gap-10 md:gap-6 items-center"
              style={{ gridTemplateColumns: "1fr" }}
            >
              <div className="hidden md:grid items-center" style={{ gridTemplateColumns: "1fr auto 1fr" }}>
                {/* LEFT pills */}
                <div className="flex flex-col gap-4 items-start pl-2">
                  {INPUTS.map(({ label, dot }, i) => (
                    <div
                      key={i}
                      ref={(el) => { inPillRefs.current[i] = el; }}
                      className="flex items-center gap-3 rounded-full px-5 py-3 bg-muted shadow-sm"
                    >
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
                      <span className="text-sm md:text-base font-semibold text-foreground whitespace-nowrap">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CENTER node */}
                <div className="flex flex-col items-center gap-3 px-8">
                  <div
                    ref={nodeRef}
                    className="flex items-center justify-center rounded-full bg-card border-2 border-border shadow-md"
                    style={{ width: "9rem", height: "9rem" }}
                  >
                    <img
                      src={isDark ? authLogoDark : authLogoLight}
                      alt="Shattara AI"
                      className="h-20 w-auto"
                    />
                  </div>
                  <p className="font-bold text-foreground leading-tight">Shattara AI</p>
                </div>

                {/* RIGHT pills */}
                <div className="flex flex-col gap-4 items-end pr-2">
                  {OUTPUTS.map((label, i) => (
                    <div
                      key={i}
                      ref={(el) => { outPillRefs.current[i] = el; }}
                      className="flex items-center gap-3 rounded-full px-5 py-3 bg-muted shadow-sm"
                    >
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-brand-green" />
                      <span className="text-sm md:text-base font-semibold text-foreground whitespace-nowrap">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* MOBILE stacked layout */}
              <div className="md:hidden flex flex-col items-center gap-8 px-2">
                <div className="flex flex-col gap-3 items-stretch w-full max-w-sm">
                  {INPUTS.map(({ label, dot }, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-full px-4 py-3 bg-muted shadow-sm">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`} />
                      <span className="text-[13px] font-semibold text-foreground leading-tight">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center justify-center rounded-full bg-card border-2 border-border shadow-md w-24 h-24">
                    <img src={isDark ? authLogoDark : authLogoLight} alt="Shattara AI" className="h-14 w-auto" />
                  </div>
                  <p className="font-bold text-foreground text-sm">Shattara AI</p>
                </div>
                <div className="flex flex-col gap-3 items-stretch w-full max-w-sm">
                  {OUTPUTS.map((label, i) => (
                    <div key={i} className="flex items-center gap-3 rounded-full px-4 py-3 bg-muted shadow-sm">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-brand-green" />
                      <span className="text-[13px] font-semibold text-foreground leading-tight">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
